import { useState } from "react";
import type { CatalogProduct } from "@/mocks/catalogData";
import CatalogProductCard from "./CatalogProductCard";

interface CatalogGridProps {
  products: CatalogProduct[];
  viewMode: "grid" | "list";
}

export default function CatalogGrid({ products, viewMode }: CatalogGridProps) {
  if (products.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <div className="w-16 h-16 flex items-center justify-center mb-4">
          <i className="ri-search-line text-4xl text-[#6C7278]" />
        </div>
        <p
          className="text-lg font-semibold text-[#1A1C1E] mb-2"
          style={{ fontFamily: "'Public Sans', sans-serif" }}
        >
          No pieces match your filters
        </p>
        <p
          className="text-sm text-[#6C7278]"
          style={{ fontFamily: "'Public Sans', sans-serif" }}
        >
          Try adjusting your category or price range.
        </p>
      </div>
    );
  }

  if (viewMode === "list") {
    return (
      <div className="flex flex-col gap-px bg-[#1A1C1E]/[0.06]">
        {products.map((product) => (
          <ListRow key={product.id} product={product} />
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-[#1A1C1E]/[0.06]" data-product-shop>
      {products.map((product) => (
        <CatalogProductCard key={product.id} product={product} />
      ))}
    </div>
  );
}

function ListRow({ product }: { product: CatalogProduct }) {
  const [liked, setLiked] = useState(false);
  const [showDetail, setShowDetail] = useState(false);

  return (
    <>
      <div
        className="group flex items-center gap-6 bg-[#F7F5F2] hover:bg-[#EFEDE9] transition-colors duration-300 p-5 cursor-pointer"
        onClick={() => setShowDetail(true)}
      >
        <div className="w-20 h-24 flex-shrink-0 overflow-hidden bg-[#EFEDE9]">
          <img
            src={product.image}
            alt={product.name}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="flex-1 min-w-0">
          <p
            className="text-[10px] uppercase tracking-[0.14em] text-[#6C7278] mb-0.5"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            {product.subcategory}
          </p>
          <h3
            className="text-sm font-semibold text-[#1A1C1E] truncate"
            style={{ fontFamily: "'Public Sans', sans-serif" }}
          >
            {product.name}
          </h3>
          <p
            className="text-xs text-[#6C7278] mt-0.5"
            style={{ fontFamily: "'Public Sans', sans-serif" }}
          >
            {product.material} &middot; {product.dimensions}
          </p>
        </div>
        <div className="flex items-center gap-6 flex-shrink-0">
          <div className="text-right hidden sm:block">
            <p
              className="text-[10px] uppercase tracking-[0.12em] text-[#6C7278]"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              Lead time
            </p>
            <p
              className="text-xs text-[#1A1C1E] mt-0.5"
              style={{ fontFamily: "'Public Sans', sans-serif" }}
            >
              {product.lead}
            </p>
          </div>
          <p
            className="text-base font-bold text-[#B8422E] w-20 text-right"
            style={{ fontFamily: "'Public Sans', sans-serif" }}
          >
            {product.priceDisplay}
          </p>
          <button
            className="w-8 h-8 flex items-center justify-center text-[#6C7278] hover:text-[#B8422E] transition-colors cursor-pointer"
            onClick={(e) => { e.stopPropagation(); setLiked(!liked); }}
          >
            <i className={liked ? "ri-heart-fill text-[#B8422E]" : "ri-heart-line"} />
          </button>
          <button
            className="hidden md:flex items-center gap-1.5 px-4 py-2 border border-[#1A1C1E]/20 text-[11px] uppercase tracking-[0.1em] text-[#1A1C1E] hover:bg-[#B8422E] hover:text-white hover:border-[#B8422E] transition-all duration-300 whitespace-nowrap cursor-pointer"
            style={{ fontFamily: "'Space Grotesk', sans-serif", borderRadius: "4px" }}
            onClick={(e) => { e.stopPropagation(); setShowDetail(true); }}
          >
            View Details
            <i className="ri-arrow-right-line text-xs" />
          </button>
        </div>
      </div>

      {showDetail && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8 bg-[#1A1C1E]/70 backdrop-blur-sm"
          onClick={() => setShowDetail(false)}
        >
          <div
            className="relative bg-[#F7F5F2] w-full max-w-3xl max-h-[90vh] overflow-y-auto flex flex-col md:flex-row"
            style={{ borderRadius: "8px" }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="absolute top-4 right-4 z-10 w-9 h-9 flex items-center justify-center bg-white/80 hover:bg-white transition-colors cursor-pointer"
              style={{ borderRadius: "4px" }}
              onClick={() => setShowDetail(false)}
            >
              <i className="ri-close-line text-[#1A1C1E] text-lg" />
            </button>
            <div className="w-full md:w-[45%] flex-shrink-0 aspect-[3/4] md:aspect-auto overflow-hidden bg-[#EFEDE9]">
              <img src={product.image} alt={product.name} className="w-full h-full object-cover object-top" />
            </div>
            <div className="flex-1 p-8 md:p-10 flex flex-col justify-between">
              <div>
                {product.badge && (
                  <span
                    className="inline-block px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.1em] bg-[#B8422E] text-white mb-4"
                    style={{ fontFamily: "'Space Grotesk', sans-serif", borderRadius: "4px" }}
                  >
                    {product.badge}
                  </span>
                )}
                <p className="text-[11px] uppercase tracking-[0.18em] text-[#6C7278] mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  {product.category} / {product.subcategory}
                </p>
                <h2 className="text-2xl md:text-3xl font-extrabold text-[#1A1C1E] tracking-[-0.02em] mb-2" style={{ fontFamily: "'Public Sans', sans-serif" }}>
                  {product.name}
                </h2>
                <p className="text-2xl font-bold text-[#B8422E] mb-6" style={{ fontFamily: "'Public Sans', sans-serif" }}>
                  {product.priceDisplay}
                </p>
                <div className="flex flex-col gap-3 mb-8">
                  {[
                    { label: "Material", value: product.material },
                    { label: "Dimensions", value: product.dimensions },
                    { label: "Lead Time", value: product.lead },
                  ].map(({ label, value }) => (
                    <div key={label} className="flex items-start gap-3">
                      <span className="text-[10px] uppercase tracking-[0.14em] text-[#6C7278] w-24 flex-shrink-0 pt-0.5" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{label}</span>
                      <span className="text-sm text-[#1A1C1E]" style={{ fontFamily: "'Public Sans', sans-serif" }}>{value}</span>
                    </div>
                  ))}
                </div>
                <div className="p-4 bg-[#1A1C1E]/[0.04] border border-[#1A1C1E]/[0.06]" style={{ borderRadius: "4px" }}>
                  <p className="text-xs text-[#6C7278] leading-relaxed" style={{ fontFamily: "'Public Sans', sans-serif" }}>
                    <i className="ri-store-2-line mr-1.5 text-[#B8422E]" />
                    Available to view in person at our West Loop showroom — 820 W. Randolph Street, Chicago.
                  </p>
                </div>
              </div>
              <div className="mt-8 flex flex-col gap-3">
                <a
                  href="/#visit"
                  className="w-full py-3 bg-[#B8422E] text-white text-[12px] font-semibold tracking-[0.1em] uppercase hover:bg-[#9E3826] transition-colors duration-300 flex items-center justify-center gap-2 whitespace-nowrap"
                  style={{ fontFamily: "'Space Grotesk', sans-serif", borderRadius: "4px" }}
                >
                  <i className="ri-map-pin-line" />
                  Book a Showroom Visit
                </a>
                <button
                  onClick={() => setLiked(!liked)}
                  className="w-full py-3 border border-[#1A1C1E]/20 text-[#1A1C1E] text-[12px] font-semibold tracking-[0.1em] uppercase hover:border-[#1A1C1E]/50 transition-colors duration-300 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                  style={{ fontFamily: "'Space Grotesk', sans-serif", borderRadius: "4px" }}
                >
                  <i className={liked ? "ri-heart-fill text-[#B8422E]" : "ri-heart-line"} />
                  {liked ? "Saved to Wishlist" : "Save to Wishlist"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
